import Button from '@loggi/ui/components/button';
import Col from '@loggi/ui/components/col';
import Display from '@loggi/ui/components/display';
import Divider from '@loggi/ui/components/divider';
import LoaderMask from '@loggi/ui/components/loader-mask';
import Text from '@loggi/ui/components/text';
import colors from '@loggi/ui/components/utils/colors';
import icons from '@loggi/ui/components/utils/icons';
import { addressDataModel, privatePackageTrackerModel } from 'app/app.models';
import getStatusPath from 'app/app.utils';
import emoji from 'images/sad.png';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import AddressText from 'shared/address-text/address-text.component';
import UserFeedbackAlert from 'shared/alert/user-feedback-alert/user-feedback-alert.component';
import {
  CATEGORY_NAME,
  CHANGE_ADDRESS_CONFIRMATION
} from 'shared/analytics/analytics.constants';
import sendGoogleAnalyticsDataEvent from 'shared/analytics/analytics.utils';
import Card from 'shared/card/card.component';
import MainView from 'shared/main-view/main-view.component';
import MapBackground from 'shared/map-background/map-background.component';
import callMutation from 'shared/relay/mutations/call-mutation';
import * as appActions from 'tracker.actions';
import {
  FEEDBACK_ALERT_ERROR_TITLE,
  RETRY_BUTTON_VALUE
} from 'tracker.constants';
import { EDIT_ADDRESS, SUCCESS } from 'tracker.routes';
import {
  BUTTON_VALUE,
  ERROR_DESCRIPTION,
  RECEIVER_IS_NOT_AUTHENTICATED
} from './confirm-edition.constants';
import confirmEditionMutation from './confirm-edition.mutation';
import styles from './confirm-edition.styles.scss';

export class ConfirmEditionContainer extends React.Component {
  static displayName = 'ConfirmEditionContainer';

  static propTypes = {
    privatePackageTracker: PropTypes.shape(privatePackageTrackerModel)
      .isRequired,
    addressData: PropTypes.shape(addressDataModel),
    newDestinationInfo: PropTypes.shape({
      firstLine: PropTypes.string,
      secondLine: PropTypes.string,
      coordinates: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number
      })
    }),
    newComplement: PropTypes.string,
    actions: PropTypes.objectOf(PropTypes.object).isRequired,
    history: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: {
        packageId: PropTypes.string
      }
    }).isRequired,
    hasFinishedScreenFlow: PropTypes.bool.isRequired
  };

  static defaultProps = {
    addressData: null,
    newDestinationInfo: null,
    newComplement: null
  };

  constructor(props) {
    super(props);

    this.state = {
      userFeedbackAlert: false,
      title: null,
      description: null,
      isLoading: false
    };
  }

  _onClickRetry = () => {
    this.setState({ userFeedbackAlert: false }, () => {
      this._sendGoogleAnalyticsDataEvent(
        CHANGE_ADDRESS_CONFIRMATION.RETRY.NAME
      );

      return this._confirmEditionMutation();
    });
  };

  _goToNextScreen = () => {
    const { actions, privatePackageTracker, history } = this.props;

    actions.savePrivatePackageTracker({
      privatePackageTracker,
      pathname: EDIT_ADDRESS
    });

    return history.push(SUCCESS);
  };

  _confirmEditionMutation = () => {
    const {
      match: {
        params: { packageId }
      },
      privatePackageTracker,
      addressData,
      newComplement
    } = this.props;

    if (!addressData && !newComplement) {
      return this._goToNextScreen();
    }

    const variables = {
      input: {
        packageId: Number(packageId),
        addressComplement:
          newComplement ||
          get(privatePackageTracker, 'destinationInfo.complement', null)
      }
    };

    if (addressData) {
      variables.input.addressData = addressData;
    }

    const callbacks = {
      onSuccess: response =>
        this._onSuccess(response.packageTrackerAddressUpdate),
      onFailure: err => this._onFailure(err)
    };

    this.setState({ isLoading: true });

    return this.setState({ isLoading: true }, () => {
      callMutation(confirmEditionMutation, variables, callbacks);
    });
  };

  _sendGoogleAnalyticsDataEvent = prop => {
    const event = {
      categoryName: CATEGORY_NAME.EDIT_ADDRESS,
      action: CHANGE_ADDRESS_CONFIRMATION[prop].ACTION,
      label: CHANGE_ADDRESS_CONFIRMATION[prop].LABEL
    };

    sendGoogleAnalyticsDataEvent(event);
  };

  _onSuccess({ success }) {
    this._sendGoogleAnalyticsDataEvent(success);
    this.setState({ isLoading: false });

    if (!success) {
      return this._setErrorAlert();
    }

    return this._goToNextScreen();
  }

  _onFailure(err) {
    this.setState({ isLoading: false });

    const { history } = this.props;

    const receiverIsNotAuthenticated = get(
      err,
      'source.errors[0].message',
      false
    );

    if (
      receiverIsNotAuthenticated &&
      receiverIsNotAuthenticated === RECEIVER_IS_NOT_AUTHENTICATED
    ) {
      this._sendGoogleAnalyticsDataEvent(
        CHANGE_ADDRESS_CONFIRMATION.RECEIVER_NOT_AUTHENTICATED.NAME
      );

      // go to Status screen:
      return history.push(this._getStatusPath());
    }

    this._sendGoogleAnalyticsDataEvent(
      CHANGE_ADDRESS_CONFIRMATION.CLIENT_OR_SERVER_ERROR.NAME
    );

    return this._setErrorAlert();
  }

  _setErrorAlert() {
    return this.setState({
      userFeedbackAlert: true,
      title: FEEDBACK_ALERT_ERROR_TITLE,
      description: ERROR_DESCRIPTION
    });
  }

  _getStatusPath() {
    const {
      match: {
        params: { packageId }
      }
    } = this.props;
    return getStatusPath(packageId);
  }

  render() {
    const {
      privatePackageTracker,
      newDestinationInfo,
      newComplement,
      history,
      hasFinishedScreenFlow
    } = this.props;

    const destinationInfo =
      newDestinationInfo || get(privatePackageTracker, 'destinationInfo', null);

    const coordinates = get(destinationInfo, 'coordinates', null);

    const complement =
      newComplement ||
      get(privatePackageTracker, 'destinationInfo.complement', null);

    if (!privatePackageTracker || !coordinates || hasFinishedScreenFlow) {
      return <Redirect to={this._getStatusPath()} />;
    }

    const firstName = get(privatePackageTracker, 'recipientInfo.firstName', '');

    const { lat, lng } = coordinates;

    const textContent = ', seu pedido será entregue aqui. Está tudo certo?';

    const buttonsProps = [
      { value: RETRY_BUTTON_VALUE, onClick: this._onClickRetry }
    ];
    const { isLoading, userFeedbackAlert, title, description } = this.state;

    return (
      <MainView className="tracker-content">
        <Display when={isLoading}>
          <LoaderMask />
        </Display>
        <MapBackground lat={lat} lng={lng} />
        <Button
          data-testid="back-btn"
          className={`${styles.customButton} pin-to-top`}
          size="large"
          icon={icons.type.SHORT_ARROW_LEFT}
          style="outline"
          shape="circle"
          onClick={() => {
            this._sendGoogleAnalyticsDataEvent(
              CHANGE_ADDRESS_CONFIRMATION.GO_BACK_TO_CHANGE_ADDRESS.NAME
            );

            history.push(EDIT_ADDRESS);
          }}
        />
        <Card
          backgroundColor={colors.type.WHITE}
          gutter="onehalf"
          className="pin-to-bottom"
        >
          <Text bold>
            {firstName}
            <Text weight="regular">{textContent}</Text>
          </Text>
          <Col gutter={{ top: 'normal', bottom: 'normal' }}>
            <Divider color={colors.type.LIGHTER} />
          </Col>
          <AddressText {...destinationInfo} complement={complement} />
          <Button value={BUTTON_VALUE} onClick={this._confirmEditionMutation} />
        </Card>
        {userFeedbackAlert && (
          <UserFeedbackAlert
            illustration={emoji}
            title={title}
            description={description}
            buttonsProps={buttonsProps}
          />
        )}
      </MainView>
    );
  }
}

function mapStateToProps(state) {
  return {
    privatePackageTracker: state.data.privatePackageTracker,
    addressData: state.packageAddress.addressData,
    newDestinationInfo: state.packageAddress.newDestinationInfo,
    newComplement: state.packageAddress.newComplement,
    hasFinishedScreenFlow: state.screenFlow.hasFinishedScreenFlow
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(appActions, dispatch)
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfirmEditionContainer);
